/**
 * Project Archive — Load More.
 *
 * Fetches the next page of case study cards from admin-ajax and appends them to
 * the grid the Project Archive template rendered (see
 * template-project-archive.php and wpbase_load_more_case_studies() in
 * inc/ajax-functions.php).
 *
 * Compiled to dist/assets/js/project-archive.js and enqueued only on pages
 * using that template — see wpbase_enqueue_project_archive_assets() in
 * inc/general.php, which is also where the endpoint and nonce in
 * wpbase_project_archive come from.
 *
 * The whole of the page state lives on the button: which page has been loaded,
 * how many cards a page holds, and how many pages there are. That is what the
 * template rendered it with, so a reload starts from the truth rather than from
 * anything this file has cached.
 *
 * The button is not rendered at all when there is only one page of results, so
 * everything below exits quietly on a page that has nothing more to fetch.
 */

const SELECTORS = {
    root: ".project-archive",
    button: ".project-archive__load-more",
    items: ".project-archive__items",
    more: ".project-archive__more",
    status: ".project-archive__status",
    label: ".button__text",
};

/**
 * A positive integer from a data attribute, or a fallback.
 *
 * Anything missing or unparseable is a template that has changed shape, not a
 * reason to send a request built on NaN.
 */
function intFrom(element, attribute, fallback) {
    const value = parseInt(element.dataset[attribute], 10);

    return Number.isFinite(value) && value > 0 ? value : fallback;
}

function initProjectArchive() {
    const root = document.querySelector(SELECTORS.root);

    if (!root) return;

    const button = root.querySelector(SELECTORS.button);
    const items = root.querySelector(SELECTORS.items);

    if (!button || !items) return;

    const params = window.wpbase_project_archive;

    // No endpoint means the enqueue did not localise — leave the button alone
    // rather than wiring a press to nothing.
    if (!params || !params.ajax_url || !params.nonce) return;

    const status = root.querySelector(SELECTORS.status);
    const label = button.querySelector(SELECTORS.label) || button;
    const restingLabel = label.textContent;

    // Translated in PHP and localised alongside the endpoint, so nothing a
    // visitor reads is written in this file. The fallbacks only stand in if the
    // localise ever changes shape.
    const text = params.i18n || {};

    let loading = false;

    /**
     * Take the button away for good.
     *
     * The whole row goes, not just the button — an empty row would leave the
     * grid sitting on a margin of nothing.
     */
    const finish = () => {
        const more = button.closest(SELECTORS.more);

        (more || button).remove();
    };

    const announce = (message) => {
        if (status) status.textContent = message;
    };

    button.addEventListener("click", () => {
        // aria-busy is the same flag the stylesheet dims the button on, so the
        // two cannot disagree about whether a request is in flight.
        if (loading) return;

        const page = intFrom(button, "page", 1) + 1;
        const perPage = intFrom(button, "perPage", 6);
        const maxPages = intFrom(button, "maxPages", 1);

        // The last page has already been loaded. Only reachable if the run
        // shrank under the visitor, so tidy up rather than ask for a page that
        // is no longer there.
        if (page > maxPages) {
            finish();
            return;
        }

        loading = true;
        button.setAttribute("aria-busy", "true");
        label.textContent = text.loading || "Loading…";

        const body = new URLSearchParams({
            action: "wpbase_load_more_case_studies",
            nonce: params.nonce,
            page: String(page),
            per_page: String(perPage),
        });

        fetch(params.ajax_url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
            body: body.toString(),
        })
            .then((response) => {
                if (!response.ok) throw new Error(response.statusText);

                return response.json();
            })
            .then((result) => {
                if (!result || !result.success || !result.data) {
                    throw new Error("Unexpected response");
                }

                items.insertAdjacentHTML("beforeend", result.data.html);
                button.dataset.page = String(result.data.page || page);

                // AOS measures the document once on load, so the cards just
                // appended are not on its list of things to watch — and, being
                // [data-aos] elements, they are sitting at opacity 0 until it
                // notices them. global.js puts the instance on window for
                // exactly this.
                if (window.AOS && typeof window.AOS.refreshHard === "function") {
                    window.AOS.refreshHard();
                }

                announce(
                    (text.loaded || "%d more projects loaded.").replace(
                        "%d",
                        result.data.count || 0,
                    ),
                );

                if (!result.data.has_more) {
                    finish();
                    announce(text.complete || "All projects loaded.");
                }
            })
            .catch(() => {
                announce(
                    text.error ||
                        "Those projects could not be loaded. Please try again.",
                );
            })
            .finally(() => {
                loading = false;
                button.removeAttribute("aria-busy");
                label.textContent = restingLabel;
            });
    });
}

document.addEventListener("DOMContentLoaded", initProjectArchive);
